import type { Node, Edge } from '@xyflow/react'

export interface ValidationError {
  type: 'cycle' | 'unreachable' | 'missing_branch' | 'broken_variable_ref'
  message: string
  nodeIds?: string[]
  edgeIds?: string[]
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
}

interface NodeData {
  id: string
  type: string
  label: string
  output_schema?: Array<{ name: string; type: string }>
  variable_mappings?: Array<{
    source_node_id: string
    source_output: string
    target_input: string
  }>
  [key: string]: unknown
}

/**
 * Validates a workflow graph for structural correctness.
 *
 * Checks performed:
 * 1. Cycle detection (DFS) -- DAG only in Phase 2
 * 2. Unreachable nodes -- any node not reachable from root nodes
 * 3. Missing branch edges -- conditional nodes with incomplete branch coverage
 * 4. Broken variable references -- variable_mappings pointing to non-existent nodes or outputs
 */
export function validateGraph(nodes: Node[], edges: Edge[]): ValidationResult {
  if (nodes.length === 0) {
    return { valid: true, errors: [] }
  }

  const errors: ValidationError[] = []
  const nodeIds = new Set(nodes.map((n) => n.id))
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  // Build adjacency list
  const adjacency = new Map<string, string[]>()
  for (const node of nodes) {
    adjacency.set(node.id, [])
  }
  for (const edge of edges) {
    if (adjacency.has(edge.source)) {
      adjacency.get(edge.source)!.push(edge.target)
    }
  }

  // 1. Cycle detection (DFS with coloring)
  const WHITE = 0
  const GRAY = 1
  const BLACK = 2
  const color = new Map<string, number>()
  const parent = new Map<string, string | null>()
  for (const id of nodeIds) {
    color.set(id, WHITE)
  }

  const cycleNodeIds = new Set<string>()

  function dfs(nodeId: string): boolean {
    color.set(nodeId, GRAY)
    const neighbors = adjacency.get(nodeId) ?? []
    for (const neighbor of neighbors) {
      if (!nodeIds.has(neighbor)) continue // skip edges to non-existent nodes
      if (color.get(neighbor) === GRAY) {
        // Found a cycle -- trace back to collect all nodes in the cycle
        cycleNodeIds.add(neighbor)
        let current: string | null | undefined = nodeId
        while (current && current !== neighbor) {
          cycleNodeIds.add(current)
          current = parent.get(current)
        }
        cycleNodeIds.add(neighbor)
        return true
      }
      if (color.get(neighbor) === WHITE) {
        parent.set(neighbor, nodeId)
        if (dfs(neighbor)) return true
      }
    }
    color.set(nodeId, BLACK)
    return false
  }

  for (const nodeId of nodeIds) {
    if (color.get(nodeId) === WHITE) {
      parent.set(nodeId, null)
      dfs(nodeId)
    }
  }

  if (cycleNodeIds.size > 0) {
    const ids = Array.from(cycleNodeIds)
    errors.push({
      type: 'cycle',
      message: `Graph contains a cycle involving nodes: ${ids.join(', ')}`,
      nodeIds: ids,
    })
  }

  // 2. Unreachable nodes
  // Root nodes = nodes with no incoming edges at all (even from non-existent sources).
  // An edge from a non-existent source still marks the target as "non-root" because
  // something intends to feed into it, but since the source doesn't exist, the target
  // becomes unreachable -- which is the correct detection.
  const hasIncoming = new Set<string>()
  for (const edge of edges) {
    if (nodeIds.has(edge.target)) {
      hasIncoming.add(edge.target)
    }
  }
  const roots = nodes.filter((n) => !hasIncoming.has(n.id)).map((n) => n.id)

  // BFS from all roots
  const reachable = new Set<string>()
  const queue = [...roots]
  while (queue.length > 0) {
    const current = queue.shift()!
    if (reachable.has(current)) continue
    reachable.add(current)
    const neighbors = adjacency.get(current) ?? []
    for (const neighbor of neighbors) {
      if (nodeIds.has(neighbor) && !reachable.has(neighbor)) {
        queue.push(neighbor)
      }
    }
  }

  const unreachable = nodes
    .filter((n) => !reachable.has(n.id))
    .map((n) => n.id)
  if (unreachable.length > 0) {
    errors.push({
      type: 'unreachable',
      message: `Unreachable nodes: ${unreachable.join(', ')}`,
      nodeIds: unreachable,
    })
  }

  // 3. Missing branch edges
  // For each node that has at least one outgoing conditional edge,
  // check that conditional edges have branch_labels.
  // A conditional edge has a non-null condition in its data.
  const outgoingBySource = new Map<string, Edge[]>()
  for (const edge of edges) {
    if (!outgoingBySource.has(edge.source)) {
      outgoingBySource.set(edge.source, [])
    }
    outgoingBySource.get(edge.source)!.push(edge)
  }

  for (const [sourceId, outEdges] of outgoingBySource) {
    const conditionalEdges = outEdges.filter(
      (e) => (e.data as Record<string, unknown>)?.condition != null
    )
    if (conditionalEdges.length > 0) {
      // Check that every conditional edge has a branch_label
      for (const edge of conditionalEdges) {
        const branchLabel = (edge.data as Record<string, unknown>)
          ?.branch_label
        if (!branchLabel) {
          errors.push({
            type: 'missing_branch',
            message: `Conditional edge from "${sourceId}" to "${edge.target}" has a condition but no branch label`,
            nodeIds: [sourceId],
            edgeIds: [edge.id],
          })
        }
      }
    }
  }

  // 4. Broken variable references
  for (const node of nodes) {
    const data = node.data as NodeData
    const mappings = data.variable_mappings
    if (!mappings || mappings.length === 0) continue

    for (const mapping of mappings) {
      const sourceNode = nodeMap.get(mapping.source_node_id)
      if (!sourceNode) {
        errors.push({
          type: 'broken_variable_ref',
          message: `Node "${node.id}" references non-existent source node "${mapping.source_node_id}"`,
          nodeIds: [node.id],
        })
        continue
      }

      const sourceData = sourceNode.data as NodeData
      const outputSchema = sourceData.output_schema ?? []
      const hasOutput = outputSchema.some(
        (o) => o.name === mapping.source_output
      )
      if (!hasOutput) {
        errors.push({
          type: 'broken_variable_ref',
          message: `Node "${node.id}" references output "${mapping.source_output}" which does not exist on node "${mapping.source_node_id}"`,
          nodeIds: [node.id, mapping.source_node_id],
        })
      }
    }
  }

  return { valid: errors.length === 0, errors }
}
